import { FEATURE } from './feature';

const hasFeature = FEATURE();

const dateInputWrapper = 'date-input-wrapper';
const dateDisplayWrapper = 'date-display-wrapper';

const signatureInputWrapper = 'signature-input-wrapper';
const signatureDisplayWrapper = 'signature-display-wrapper';

const textareaInputWrapper = 'textarea-input-wrapper';
const textareaDisplayWrapper = 'textarea-display-wrapper';

const hiddenSpaceWrapper = 'hidden-space-wrapper';

const hiddenElementSelector = 'd-none';
const hiddenElementSelectors = [hiddenElementSelector, 'hidden'];

export const JSPDFOutputType = {
  arraybuffer: 'arraybuffer',
  blob: 'blob',
  bloburi: 'bloburi',
  datauristring: 'datauristring',
};

/**
 * @description The `function` show hides all the required HTML elements before starting the pdf generation.
 * @param {string} page 
 * @param {string} selector 
 * @returns {{
 *  pages: {
 *    element: Element,
 *    isHidden: boolean,
 *    index: number,
 *    dateInputs: NodeListOf<Element>,
 *    dateDisplays: NodeListOf<Element>,
 *    textareaInputs: NodeListOf<Element>,
 *    textareaDisplays: NodeListOf<Element>,
 *    signatureInputs: NodeListOf<Element>,
 *    signatureDisplays: NodeListOf<Element>,
 *    hiddenSpaces: NodeListOf<Element>,
 *  }[],
 *  hasError: boolean,
 * }}
 */
const makePageReady = (page, selector) => {
  try {
    /**
     * @type {{
     *    element: Element,
     *    isHidden: boolean,
     *    index: number,
     *    dateInputs: NodeListOf<Element>,
     *    dateDisplays: NodeListOf<Element>,
     *    textareaInputs: NodeListOf<Element>,
     *    textareaDisplays: NodeListOf<Element>,
     *    signatureInputs: NodeListOf<Element>,
     *    signatureDisplays: NodeListOf<Element>,
     *    hiddenSpaces: NodeListOf<Element>,
     * }[]}
     */
    const pages = [];
    const pagesNodeList = document.querySelectorAll(`.${page}`);
    pagesNodeList.forEach((element, index) => {
      const isHidden = element.classList.contains(hiddenElementSelector);
      pages.push({
        element,
        isHidden,
        index,
      });
      element.classList.remove(...hiddenElementSelectors);
    });

    /**
     * @type {NodeListOf<Element>}
     */
    let dateInputs = [];
    /**
     * @type {NodeListOf<Element>}
     */
    let dateDisplays = [];
    /**
     * @type {NodeListOf<Element>}
     */
    let textareaInputs = [];
    /**
     * @type {NodeListOf<Element>}
     */
    let textareaDisplays = [];
    /**
     * @type {NodeListOf<Element>}
     */
    let signatureInputs = [];
    /**
     * @type {NodeListOf<Element>}
     */
    let signatureDisplays = [];
    /**
     * @type {NodeListOf<Element>}
     */
    let hiddenSpaces = [];

    pagesNodeList.forEach((element, index) => {
      // adds the d-none class
      dateInputs = element.querySelectorAll(`.${dateInputWrapper}`);
      dateInputs.forEach((input) => input.classList.add(hiddenElementSelector));

      // removes the d-none class
      dateDisplays = element.querySelectorAll(`.${dateDisplayWrapper}`);
      dateDisplays.forEach((input) => input.classList.remove(hiddenElementSelector));

      // adds the d-none class
      signatureInputs = element.querySelectorAll(`.${signatureInputWrapper}`);
      signatureInputs.forEach((input) => input.classList.add(hiddenElementSelector));

      // removes the d-none class
      signatureDisplays = element.querySelectorAll(`.${signatureDisplayWrapper}`);
      signatureDisplays.forEach((input) => input.classList.remove(hiddenElementSelector));

      // adds the d-none class
      textareaInputs = element.querySelectorAll(`.${textareaInputWrapper}`);
      textareaInputs.forEach((input) => input.classList.add(hiddenElementSelector));

      // removes the d-none class
      textareaDisplays = element.querySelectorAll(`.${textareaDisplayWrapper}`);
      textareaDisplays.forEach((input) => input.classList.remove(hiddenElementSelector));

      // shows the hidden spaces used for Axena template
      hiddenSpaces = element.querySelectorAll(`.${hiddenSpaceWrapper}`);
      hiddenSpaces.forEach((input) => input.classList.remove(hiddenElementSelector));

      const page = pages[index];
      page.dateInputs = dateInputs;
      page.dateDisplays = dateDisplays;
      page.textareaInputs = textareaInputs;
      page.textareaDisplays = textareaDisplays;
      page.signatureInputs = signatureInputs;
      page.signatureDisplays = signatureDisplays;
      page.hiddenSpaces = hiddenSpaces;
    });

    return {
      pages,
      hasError: false,
    };
  } catch (e) {
    return {
      pages: [],
      hasError: true,
    };
  }
};

/**
 * @description The `function` resets the page after pdf generation.
 * @param {{
 *  element: Element,
 *  isHidden: boolean,
 *  dateInputs: NodeListOf<Element>,
 *  dateDisplays: NodeListOf<Element>,
 *  textareaInputs: NodeListOf<Element>,
 *  textareaDisplays: NodeListOf<Element>,
 *  signatureInputs: NodeListOf<Element>,
 *  signatureDisplays: NodeListOf<Element>,
 *  hiddenSpaces: NodeListOf<Element>,
 * }[]} pages
 * @returns {string} output
 */
const resetPage = (pages) => {
  try {
    pages.forEach((_p) => {
      if (_p.isHidden) {
        _p.element.classList.add(hiddenElementSelector);
      }

      // removes the d-none class
      _p.dateInputs.forEach((input) => input.classList.remove(hiddenElementSelector));
      // adds the d-none class
      _p.dateDisplays.forEach((input) => input.classList.add(hiddenElementSelector));
      // removes the d-none class
      _p.textareaInputs.forEach((input) => input.classList.remove(hiddenElementSelector));
      // adds the d-none class
      _p.textareaDisplays.forEach((input) => input.classList.add(hiddenElementSelector));
      // removes the d-none class
      _p.signatureInputs.forEach((input) => input.classList.remove(hiddenElementSelector));
      // adds the d-none class
      _p.signatureDisplays.forEach((input) => input.classList.add(hiddenElementSelector));
      // hides the extra spaces
      _p.hiddenSpaces.forEach((input) => input.classList.add(hiddenElementSelector));
    });
  } catch (e) {
    // 
  }

  return '';
};

/**
 * @description The `function` generates the PDF for all generic templates.
 * @param {string} name 
 * @param {string[]} pageSelectors 
 * @param {Letter} pageOptions 
 * @param {string} selector 
 * @param {number} quality 
 * @param {function} generatePDF
 * @returns {string} output
 */
export const preparePDF = async (name, pageSelectors, pageOptions, selector, quality, generatePDF) => {
  /**
   * @type {{
   *  element: Element;
   *  isHidden: boolean;
   *  dateInputs: NodeListOf<Element>;
   *  dateDisplays: NodeListOf<Element>;
   *  signatureInputs: NodeListOf<Element>;
   *  signatureDisplays: NodeListOf<Element>;
   *  hiddenSpaces: NodeListOf<Element>;
   * }[]}
   */
  const pages = [];
  for (let i = 0; i < pageSelectors.length; i++) {
    const page = pageSelectors[i];
    const { hasError, pages: _pages } = makePageReady(page, selector);
    if (hasError) {
      return resetPage(_pages);
    }
    pages.push(..._pages);
  }

  try {
    const pdf = await generatePDF(pageSelectors, pageOptions, selector, quality);

    if (hasFeature.DOWNLOAD_CMN_PDF) {
      const time = new Date().getTime();
      pdf.save(`${name}-${time}.pdf`);
    }

    resetPage(pages);

    // base64 image
    const output = pdf.output(JSPDFOutputType.datauristring);
    return output;
  } catch (e) {
    return resetPage(pages);
  }
};
